'use client';

import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  MessageSquare,
  Clock,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Stethoscope,
  User,
  Plus,
} from 'lucide-react';

interface Message {
  id: number;
  from: 'patient' | 'doctor';
  text: string;
  time: string;
  read: boolean;
}

interface PastThread {
  id: number;
  question: string;
  date: string;
  answered: boolean;
  messages: Message[];
}

const pastThreads: PastThread[] = [
  {
    id: 1,
    question: 'Can I take Salbutamol more than 4 times a day during an episode?',
    date: '05 Aug 2026',
    answered: true,
    messages: [
      { id: 1, from: 'patient', text: 'Can I take Salbutamol more than 4 times a day during an episode?', time: '05 Aug, 10:12 AM', read: true },
      { id: 2, from: 'doctor', text: 'If you need Salbutamol more than 4 times in 24 hours, it usually means your COPD is not well-controlled. Please come in for a review or call the clinic. Do not exceed 4 puffs without medical supervision. If you feel very breathless or your SpO2 drops below 90%, go to the emergency room immediately.', time: '05 Aug, 11:30 AM', read: true },
    ],
  },
  {
    id: 2,
    question: 'Is it safe to travel to higher altitude areas like Patnitop with COPD?',
    date: '20 Jul 2026',
    answered: true,
    messages: [
      { id: 1, from: 'patient', text: 'Is it safe to travel to higher altitude areas like Patnitop with COPD?', time: '20 Jul, 09:00 AM', read: true },
      { id: 2, from: 'doctor', text: 'Travel to high altitude with COPD requires caution. At Patnitop (~2000m), oxygen levels are lower, which can worsen breathlessness. Carry your rescue inhaler and ensure your SpO2 is stable before travel. Avoid strenuous activity. If your FEV1 is below 50%, consult me before planning the trip.', time: '20 Jul, 02:15 PM', read: true },
    ],
  },
];

const quickQuestions = [
  'When should I use my rescue inhaler?',
  'What are warning signs of a COPD flare-up?',
  'Can I exercise with COPD?',
  'How do I know if my inhaler is empty?',
];

export default function AskQuestionPage() {
  const [newQuestion, setNewQuestion] = useState('');
  const [sent, setSent] = useState(false);
  const [expandedThread, setExpandedThread] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'ask' | 'history'>('ask');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewQuestion(e.target.value);
    setCharCount(e.target.value.length);
  };

  const handleSend = () => {
    if (!newQuestion.trim()) return;
    setSent(true);
  };

  const handleQuickQuestion = (q: string) => {
    setNewQuestion(q);
    setCharCount(q.length);
    textareaRef.current?.focus();
  };

  useEffect(() => {
    if (sent) {
      const t = setTimeout(() => {
        setSent(false);
        setNewQuestion('');
        setCharCount(0);
        setActiveTab('history');
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [sent]);

  if (sent) {
    return (
      <div className="max-w-lg mx-auto px-4 pt-16 flex flex-col items-center text-center space-y-4">
        <div className="w-20 h-20 bg-positive-bg rounded-full flex items-center justify-center">
          <CheckCheck size={36} className="text-positive" />
        </div>
        <h2 className="text-xl font-800 text-foreground">Question Sent!</h2>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
          Your question has been sent to Dr. Neeraj Mumwalia. You will receive a reply within 24 hours.
        </p>
        <div className="flex items-center gap-2 bg-info-bg border border-info/20 rounded-xl px-4 py-3 mt-2">
          <AlertCircle size={14} className="text-info shrink-0" />
          <p className="text-xs text-info font-500">For emergencies, call the clinic directly or visit the nearest hospital.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 pt-6 space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-800 text-foreground">Ask a Question</h1>
        <p className="text-sm text-muted-foreground mt-1">Send a message to Dr. Neeraj Mumwalia</p>
      </div>

      {/* Doctor Info */}
      <div className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4">
        <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center shrink-0">
          <Stethoscope size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <p className="font-700 text-foreground text-sm">Dr. Neeraj Mumwalia</p>
          <p className="text-xs text-muted-foreground mt-0.5">Pulmonologist · Centre for Lung Care and Chest Diseases, Jammu</p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 bg-positive rounded-full" />
          <span className="text-[10px] font-600 text-positive">Responds in 24h</span>
        </div>
      </div>

      {/* Emergency Notice */}
      <div className="flex items-start gap-3 bg-danger-bg border border-danger/20 rounded-xl px-4 py-3">
        <AlertCircle size={15} className="text-danger shrink-0 mt-0.5" />
        <p className="text-xs text-danger font-500 leading-relaxed">
          <strong>Emergency?</strong> Do not use this form. Call the clinic at <strong>+91 191-XXXXXXX</strong> or go to the nearest hospital immediately.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex bg-muted rounded-xl p-1 gap-1">
        {(['ask', 'history'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 text-sm font-600 rounded-lg transition-all ${
              activeTab === tab ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
            }`}
          >
            {tab === 'ask' ? 'New Question' : `History (${pastThreads.length})`}
          </button>
        ))}
      </div>

      {activeTab === 'ask' ? (
        <div className="space-y-4">
          {/* Quick Questions */}
          <div>
            <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-2">Common Questions</p>
            <div className="flex flex-wrap gap-2">
              {quickQuestions.map((q) => (
                <button
                  key={q}
                  onClick={() => handleQuickQuestion(q)}
                  className="flex items-center gap-1.5 bg-secondary text-primary text-xs font-600 px-3 py-1.5 rounded-full border border-primary/20 hover:bg-primary hover:text-white transition-all"
                >
                  <Plus size={11} /> {q}
                </button>
              ))}
            </div>
          </div>

          {/* Question Input */}
          <div>
            <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-2">Your Question</p>
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={newQuestion}
                onChange={handleChange}
                placeholder="Type your question here… Be as specific as possible about your symptoms, medications, or concerns."
                rows={5}
                maxLength={500}
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
              <span className="absolute bottom-3 right-3 text-[10px] text-muted-foreground tabular-nums">
                {charCount}/500
              </span>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-secondary border border-primary/10 rounded-xl p-4 space-y-2">
            <p className="text-xs font-700 text-primary">Tips for a better response:</p>
            {[
              'Mention your current medications',
              'Describe when symptoms started',
              'Include your SpO₂ reading if relevant',
            ].map((tip, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                <p className="text-xs text-muted-foreground">{tip}</p>
              </div>
            ))}
          </div>

          <button
            onClick={handleSend}
            disabled={!newQuestion.trim()}
            className="w-full flex items-center justify-center gap-2 bg-primary text-white font-700 text-sm py-3.5 rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={16} /> Send Question
          </button>
        </div>
      ) : (
        /* History Tab */
        <div className="space-y-3">
          {pastThreads.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">No past questions yet</div>
          ) : (
            pastThreads.map((thread) => (
              <div key={thread.id} className="bg-card border border-border rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedThread(expandedThread === thread.id ? null : thread.id)}
                  className="w-full flex items-start gap-3 p-4 text-left"
                >
                  <div className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <MessageSquare size={14} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-600 text-foreground leading-snug line-clamp-2">{thread.question}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Clock size={10} /> {thread.date}
                      </span>
                      {thread.answered && (
                        <span className="flex items-center gap-1 text-[10px] text-positive font-600">
                          <CheckCheck size={10} /> Answered
                        </span>
                      )}
                    </div>
                  </div>
                  {expandedThread === thread.id
                    ? <ChevronUp size={16} className="text-muted-foreground shrink-0 mt-1" />
                    : <ChevronDown size={16} className="text-muted-foreground shrink-0 mt-1" />
                  }
                </button>

                {expandedThread === thread.id && (
                  <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
                    {thread.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex gap-2.5 ${msg.from === 'patient' ? 'flex-row-reverse' : 'flex-row'}`}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                          msg.from === 'doctor' ? 'bg-primary' : 'bg-muted'
                        }`}>
                          {msg.from === 'doctor'
                            ? <Stethoscope size={13} className="text-white" />
                            : <User size={13} className="text-muted-foreground" />
                          }
                        </div>
                        <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${
                          msg.from === 'patient' ?'bg-primary text-white rounded-tr-sm' :'bg-muted text-foreground rounded-tl-sm'
                        }`}>
                          <p className="text-xs leading-relaxed">{msg.text}</p>
                          <p className={`text-[10px] mt-1 ${msg.from === 'patient' ? 'text-white/60' : 'text-muted-foreground'}`}>
                            {msg.time}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
